/**
 * Map marker class for creating and managing country markers on the map
 */

import { getTierColor } from '../../services/TierColorService';

/**
 * Class to create and manage map markers
 */
class MapMarker {
  /**
   * Create a new map marker
   * @param {Object} options - Configuration options
   * @param {Object} options.country - Country data
   * @param {number} options.maxTotal - Maximum total value for all countries
   * @param {number} options.maxLargest - Maximum largest value for all countries
   * @param {number} options.zoom - Current map zoom level
   * @param {Object} options.L - Leaflet library instance
   * @param {Object} options.layer - Map layer to add the marker to
   * @param {string|null} options.activeTierFilter - Active tier filter for color changes
   * @param {boolean} options.showTimezones - Whether timezone information should be shown
   * @param {boolean} options.useTierColors - Whether to color markers by their tier
   * @param {Function} options.onSelect - Callback when marker is selected
   */
  constructor({ country, maxTotal, maxLargest, zoom, L, layer, activeTierFilter, showTimezones, clientData, useTierColors, onSelect }) {
    this.country = country;
    this.maxTotal = maxTotal;
    this.maxLargest = maxLargest;
    this.zoom = zoom;
    this.L = L;
    this.layer = layer;
    this.activeTierFilter = activeTierFilter;
    this.showTimezones = showTimezones;
    this.clientData = clientData;
    this.useTierColors = useTierColors;
    this.onSelect = onSelect;
    
    // Set colors based on tier filtering or tier coloring mode
    if (activeTierFilter) {
      // Use tier-specific colors when tier filtering is active
      this.colors = {
        light: getTierColor(activeTierFilter, 'light'),
        dark: getTierColor(activeTierFilter, 'dark')
      };
    } else if (useTierColors) {
      // Use tier colors based on the dominant tier for this country
      const dominantTier = this.getDominantTier();
      this.colors = {
        light: getTierColor(dominantTier, 'light'),
        dark: getTierColor(dominantTier, 'dark')
      };
    } else {
      // Default blue colors when no tier filtering or tier coloring
      this.colors = {
        light: '#b3d9ff', // Light blue for outer circle
        dark: '#0074D9'   // Medium blue for inner circle
      };
    }
  }
  
  /**
   * Get the dominant tier for this country based on client data
   * @returns {string} The dominant tier for this country
   */
  getDominantTier() {
    if (!this.clientData || !this.clientData.length) {
      return 'Unknown';
    }
    
    // Find clients from this country
    const countryClients = this.clientData.filter(client => 
      client.country === this.country.country
    );
    
    if (countryClients.length === 0) {
      return 'Unknown';
    }
    
    // Count tiers and find the most common one
    const tierCounts = {};
    countryClients.forEach(client => {
      const tier = client['Tier'] || 'Unknown';
      tierCounts[tier] = (tierCounts[tier] || 0) + 1;
    });
    
    // Find the tier with the highest count
    let dominantTier = 'Unknown';
    let maxCount = 0;
    Object.entries(tierCounts).forEach(([tier, count]) => {
      if (count > maxCount) {
        maxCount = count;
        dominantTier = tier;
      }
    });
    
    return dominantTier;
  }
  
  /**
   * Calculate the radius for a circle based on value
   * @param {number} value - Value to scale
   * @param {number} maxValue - Maximum value for scaling
   * @param {number} minRadius - Minimum radius
   * @param {number} scale - Scale factor
   * @returns {number} Calculated radius
   */
  calcRadius(value, maxValue, minRadius, scale) {
    const base = minRadius + (value / maxValue) * scale;
    
    // Progressive scaling to prevent map clutter at low zoom levels:
    // Zoom 3+: Normal behavior (factor = 2/3 = 0.67)
    // Zoom 2: Very small circles (factor = 2/20 = 0.1) 
    // Zoom 1: Extremely tiny circles (factor = 2/40 = 0.05)
    let effZ;
    let adjustedMinRadius = minRadius;
    
    if (this.zoom >= 3) {
      effZ = 3; // Normal scaling for zoom 3+
    } else if (this.zoom === 2) {
      effZ = 20; // Very small circles for world view
      adjustedMinRadius = Math.min(minRadius, 2); // Much smaller minimum for zoom 2
    } else {
      effZ = 40; // Extremely tiny circles for maximum zoom out
      adjustedMinRadius = Math.min(minRadius, 1); // Tiny minimum for zoom 1
    }
    
    const factor = 2 / Math.max(effZ, 1);
    return Math.max(adjustedMinRadius, Math.min(base * factor, scale));
  }
  
  /**
   * Get timezone information for this country from client data
   * @returns {Object} Timezone information
   */
  getCountryTimezone() {
    if (!this.clientData || !this.clientData.length) {
      return { timezone: 'N/A', localTime: 'N/A' };
    }
    
    // Find clients from this country
    const countryClients = this.clientData.filter(client => 
      client.country === this.country.country
    );
    
    if (countryClients.length === 0) {
      return { timezone: 'N/A', localTime: 'N/A' };
    }
    
    // Get timezone from the first client (they should all be the same for the same country)
    const firstClient = countryClients[0];
    const timezone = firstClient['Time zone'] || 'N/A';
    
    // Calculate local time if we have timezone info
    let localTime = 'N/A';
    if (timezone && timezone !== 'N/A') {
      try {
        const now = new Date();
        // Try to parse timezone format (e.g., "UTC+8", "UTC-5")
        if (timezone.startsWith('UTC')) {
          const offsetStr = timezone.substring(3);
          if (offsetStr) {
            const offsetHours = parseFloat(offsetStr);
            const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
            const localTimeMs = utcTime + (offsetHours * 3600000);
            const localDate = new Date(localTimeMs);
            
            localTime = localDate.toLocaleString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
              weekday: 'short'
            });
          }
        }
      } catch (error) {
        console.warn('Error calculating local time:', error);
        localTime = 'Time unavailable';
      }
    }
    
    return { timezone, localTime };
  }
  
  /**
   * Create popup HTML content
   * @returns {string} HTML content for popup
   */
  createPopupContent() {
    const { country, dominantManager, managers, total, largest } = this.country;
    const others = managers.filter(m => m !== dominantManager);
    
    let popupHtml = `
      <div class="info-popup">
        <h3 style="margin: 0 0 8px 0; font-size: 16px; color: #333; font-weight: 600;">${country}</h3>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Total Groups:</strong> ${total}</p>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Largest Group:</strong> ${largest}</p>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Largest Manager:</strong> <span style="color:${this.colors.dark}; font-weight:bold;">${dominantManager}</span></p>
    `;
    
    if (others.length) {
      popupHtml += `<p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Other Managers:</strong> ${others.join(', ')}</p>`;
    }
    
    // Add timezone information if Show Zones is active
    if (this.showTimezones) {
      const timezoneInfo = this.getCountryTimezone();
      popupHtml += `
        <hr style="margin: 8px 0; border: none; border-top: 1px solid #ddd;">
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Timezone:</strong> <span style="color: #0074D9; font-weight: bold;">${timezoneInfo.timezone}</span></p>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Local Time:</strong> ${timezoneInfo.localTime}</p>
      `;
    }
    
    popupHtml += `</div>`;
    return popupHtml;
  }
  
  /**
   * Add the marker to the map
   */
  addToMap() {
    const { L, layer, country, maxTotal, maxLargest, onSelect } = this;
    const { latitude, longitude } = country;
    
    // Calculate circle radii
    const outerRadius = this.calcRadius(country.total, maxTotal, 8, 25);
    const innerRadius = this.calcRadius(country.largest, maxLargest, 6, 15);
    
    // Create outer circle (total clients)
    const outer = L.circleMarker([latitude, longitude], { 
      radius: outerRadius, 
      fillColor: this.colors.light, 
      color: '#333', 
      weight: 1.5, 
      fillOpacity: 0.8 
    }).addTo(layer);
    
    // Create inner circle (largest client)
    const inner = L.circleMarker([latitude, longitude], { 
      radius: Math.min(innerRadius, outerRadius - 2), 
      fillColor: this.colors.dark, 
      color: '#333', 
      weight: 0.5, 
      fillOpacity: 0.9 
    }).addTo(layer);
    
    // Create popup
    const popupContent = this.createPopupContent();
    const popup = L.popup().setContent(popupContent);
    
    // Bind popup to both circles
    outer.bindPopup(popup);
    inner.bindPopup(popup);
    
    // Add click handlers
    outer.on('click', onSelect);
    inner.on('click', onSelect);
    
    // Store circle references
    this.outer = outer;
    this.inner = inner;
    this.popup = popup;
    
    return this;
  }
  
  /**
   * Remove the marker from the map
   */
  remove() {
    if (this.outer) this.outer.remove();
    if (this.inner) this.inner.remove();
  }
}

export default MapMarker;
